import { yupResolver } from "@hookform/resolvers/yup";
import { LoadingButton } from "@mui/lab";
import { Grid } from "@mui/material";
import { isRejected } from "@reduxjs/toolkit";
import PasswordField from "components/PasswordField";
import getErrorDetailTranslationKey from "helpers/errors/getErrorDetailTranslationKey";
import useAlertBox from "hooks/useAlertBox";
import useFormValidation from "hooks/useFormValidation";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "store";
import { changePassword } from "store/slices/auth/authSlice";
import { isAxiosErrorOfResponseType } from "types/helpers";
import { notify } from "utils/SnackbarProvider";
import * as yup from "yup";

const resetPasswordSchema = yup.object({
  oldPassword: yup.string().label("currentPassword").required(),
  newPassword: yup.string().label("newPassword").required().max(255).min(8),
  confirmNewPassword: yup
    .string()
    .label("confirmPassword")
    .equalsTo("newPassword", "passwordsMustMatch")
    .required(),
});

type ProfileResetPasswordInputs = yup.InferType<typeof resetPasswordSchema>;

export default function ProfileResetPasswordForm() {
  const { t } = useTranslation(["auth", "errors"]);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [isRequestPending, setIsRequestPending] = useState(false);

  const { AlertBox, setAlert, clearAlert } = useAlertBox();

  const { handleSubmit, formState, register } = useForm<ProfileResetPasswordInputs>({
    resolver: yupResolver(resetPasswordSchema),

    mode: "onBlur",
    reValidateMode: "onBlur",
  });

  const registerValidation = useFormValidation(formState, { ns: "auth" });

  const attemptChangePassword = handleSubmit(({ oldPassword, newPassword, confirmNewPassword }) => {
    setIsRequestPending(true);
    clearAlert();

    dispatch(changePassword({ oldPassword, newPassword, confirmNewPassword })).then((action) => {
      setIsRequestPending(false);

      if (isRejected(action)) {
        if (isAxiosErrorOfResponseType(action.payload) && action.payload.response) {
          const responseData = action.payload.response.data;

          if (responseData.detail) {
            const errorMessageTranslationKey = getErrorDetailTranslationKey(responseData.detail);

            setAlert({
              message: t(errorMessageTranslationKey),
              severity: "error",
            });
          }
        }
      } else {
        navigate("/profile/edit");

        notify(t("passwordIsSuccessfullyUpdated"), "success", {
          shouldPersist: true,
        });
      }
    });
  });

  return (
    <>
      <Grid item xs={12}>
        <AlertBox />
      </Grid>

      <Grid item xs={12} container spacing={3} component="form" onSubmit={attemptChangePassword}>
        <Grid item xs={12}>
          <PasswordField
            fullWidth
            label={t("currentPassword")}
            placeholder={t("currentPasswordPlaceholder")}
            {...register("oldPassword")}
            {...registerValidation("oldPassword")}
          />
        </Grid>

        <Grid item xs={12}>
          <PasswordField
            fullWidth
            label={t("newPassword")}
            placeholder={t("newPasswordPlaceholder")}
            {...register("newPassword")}
            {...registerValidation("newPassword")}
          />
        </Grid>

        <Grid item xs={12}>
          <PasswordField
            fullWidth
            label={t("confirmPassword")}
            placeholder={t("confirmPasswordPlaceholder")}
            {...register("confirmNewPassword")}
            {...registerValidation("confirmNewPassword")}
          />
        </Grid>

        <Grid item xs={12}>
          <LoadingButton fullWidth type="submit" loading={isRequestPending} size="medium">
            {t("changePassword")}
          </LoadingButton>
        </Grid>
      </Grid>
    </>
  );
}
